import { FieldArray, Formik, useFormikContext } from "formik";
import { useEffect, useState } from "react";
import apiRequest from "../../../../utils/admin/apiRequest";
import InputField from "../../../ui/form/InputField";
import Button from "../../../ui/utils/Button";
import SelectField from "../../../ui/form/SelectField";
import usePost from "../../../../hooks/api/usePost";
import useFetch from "../../../../hooks/api/useFetch";
import Alert from "../../../ui/utils/Alert";
import { adminTowerUpdateValidationSchema } from "../../../../validation/admin/authValidation";
import { CgAdd } from "react-icons/cg";
import { Trash } from "lucide-react";
import AsyncSearchSelectProfile from "../../../ui/form/AsyncSearchSelectProfile";


const LocationWatcher = ({
  data,
  fetchStates,
  fetchCities,
  setBlankResponse,
  setCityBlankResponse,
}) => {
  const { values, setFieldValue } = useFormikContext();
  const isEditMode = !!data?._id;

  useEffect(() => {
    if (data?.countryId?._id) {
      fetchStates({ countryId: data.countryId._id });
    }
    if (data?.stateId?._id) {
      fetchCities({ stateId: data.stateId._id });
    }
  }, []);

  useEffect(() => {
    if (values.countryId) {
      fetchStates({ countryId: values.countryId });
      if (!isEditMode) {
        setFieldValue("stateId", "");
        setFieldValue("cityId", "");
      }
    } else {
      setBlankResponse();
    }
  }, [values.countryId]);

  useEffect(() => {
    if (values.stateId) {
      fetchCities({ stateId: values.stateId });
      if (!isEditMode) {
        setFieldValue("cityId", "");
      }
    } else {
      setCityBlankResponse();
    }
  }, [values.stateId]);

  return null;
};


const EditStaffAdmin = ({ closeModal, callback, data }) => {
  const { error, isLoading: loading, post, serverErrors } = usePost(apiRequest.editCorporateAdmin);

  const [selectedCorporateOptions, setSelectedCorporateOptions] = useState([]);



  const initialValues = {
    name: data?.name || "",
    countryId: data?.countryId?._id || "",
    cityId: data?.cityId?._id || "",
    stateId: data?.stateId?._id || "",
    corporateCodeId: data?.corporateCodeId?.length
      ? data.corporateCodeId.map((c) => c._id)
      : [""],
    mobile: data?.mobile || "",
    email: data?.email || "",
    contactPerson: data?.contactPerson || "",
    password: "",
    userId: data?.userId || "",
  };

  useEffect(() => {
    if (data?.corporateCodeId?.length) {
      setSelectedCorporateOptions(
        data.corporateCodeId.map((c) => ({
          label: `${c.corporateName} (${c.corporateCode})`,
          value: c._id,
        }))
      );
    }
  }, [data]);


  const { data: countryData } = useFetch({
    apiMethod: apiRequest.getCountry,
    body: { paginate: false, sortField: "name", sortOrder: "asc" },
  });

  const { data: stateData, refetch: fetchStates, setBlankResponse } = useFetch({
    apiMethod: apiRequest.getStateByCountryId,
    body: { paginate: false, sortField: "name", sortOrder: "asc" },
    autoFetch: false,
  });

  const {
    data: cityData,
    refetch: fetchCities,
    setBlankResponse: setCityBlankResponse,
  } = useFetch({
    apiMethod: apiRequest.getCityByState,
    body: { paginate: false, sortField: "name", sortOrder: "asc" },
    autoFetch: false,
  });
  const fetchCorporateCodes = async (search = "") => {
    const res = await apiRequest.getCorporateCodeForSelect({
      body: { search, limit: 20 },
    });

    return (
      res?.data?.data?.corporatecodes?.map((v) => ({
        label: `${v.name} (${v.code})`,
        value: v._id,
      })) || []
    );
  };


  return (
    <div>
      <h2 className="p-4 text-lg font-semibold">Edit Tower Admin</h2>
      <div className="border-b" />

      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={adminTowerUpdateValidationSchema}
        onSubmit={async (values) => {
          const response = await post({ data: { body: values, id: data?._id } });

          if (response.response) {
            callback?.();
          }
        }}
      >
        {({ values, handleBlur, handleChange, handleSubmit, touched, errors, setFieldValue }) => (
          <>
            <LocationWatcher
              fetchStates={fetchStates}
              fetchCities={fetchCities}
              setBlankResponse={setBlankResponse}
              setCityBlankResponse={setCityBlankResponse}
            />

            <form onSubmit={handleSubmit} className="text-sm max-h-[80vh] overflow-auto">
              {error && <Alert message={error} type="error" />}

              <div className="grid grid-cols-1 gap-2 p-4 sm:grid-cols-2">

                <InputField
                  name="name"
                  label="Tower Admin Name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.name && errors.name}
                  serverError={serverErrors?.name}
                />

                <InputField
                  name="mobile"
                  label="Mobile"
                  value={values.mobile}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.mobile && errors.mobile}
                  serverError={serverErrors?.mobile}
                />

                <InputField
                  name="email"
                  label="Email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.email && errors.email}
                  serverError={serverErrors?.email}
                />

                <SelectField
                  name="countryId"
                  label="Country"
                  value={values.countryId}
                  onBlur={handleBlur}
                  error={touched.countryId && errors.countryId}
                  serverError={serverErrors?.countryId}
                  onChange={handleChange}
                  options={countryData?.countries?.map((c) => ({
                    value: c._id,
                    label: c.name,
                  }))}
                />

                <SelectField
                  name="stateId"
                  label="State"
                  value={values.stateId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.stateId && errors.stateId}
                  serverError={serverErrors?.stateId}
                  options={stateData?.states?.map((s) => ({
                    value: s._id,
                    label: s.name,
                  }))}
                />

                <SelectField
                  name="cityId"
                  label="City"
                  value={values.cityId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.cityId && errors.cityId}
                  serverError={serverErrors?.cityId}
                  options={cityData?.cities?.map((c) => ({
                    value: c._id,
                    label: c.name,
                  }))}
                />

                <InputField
                  name="contactPerson"
                  label="Contact Person"
                  value={values.contactPerson}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.contactPerson && errors.contactPerson}
                  serverError={serverErrors?.contactPerson}
                />

                <InputField
                  name="userId"
                  label="User ID"
                  value={values.userId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.userId && errors.userId}
                  serverError={serverErrors?.userId}
                />

                <InputField
                  type="password"
                  name="password"
                  label="Password (Leave empty to keep old)"
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.password && errors.password}
                  serverError={serverErrors?.password}
                />

                <div className="col-span-full">
                  <FieldArray name="corporateCodeId">
                    {({ push, remove }) => (
                      <>
                        {values.corporateCodeId.map((_, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-2 mb-2"
                          >
                            <AsyncSearchSelectProfile
                              placeholder="Select Corporate Code"
                              value={selectedCorporateOptions[index] || null}
                              fetchOptions={fetchCorporateCodes}
                              onChange={(option) => {
                                setFieldValue(`corporateCodeId.${index}`, option?.value || "");

                                const updated = [...selectedCorporateOptions];
                                updated[index] = option;
                                setSelectedCorporateOptions(updated);
                              }}
                            />


                            <button
                              type="button"
                              onClick={() => {
                                remove(index);
                                setSelectedCorporateOptions((prev) =>
                                  prev.filter((_, i) => i !== index)
                                );
                              }}
                              className="text-red-500"
                            >
                              <Trash size={16} />
                            </button>
                          </div>
                        ))}

                        <button
                          type="button"
                          onClick={() => {
                            push("");
                            setSelectedCorporateOptions((prev) => [
                              ...prev,
                              null,
                            ]);
                          }}
                          className="mt-2 px-2 py-1 bg-blue-500 text-white text-xs rounded flex items-center gap-2"
                        >
                          <CgAdd /> Add Corporate Code
                        </button>
                      </>
                    )}
                  </FieldArray>
                </div>
              </div>

              <div className="border-b" />

              <div className="flex justify-end gap-2 p-4">
                <Button variant="secondary" onClick={closeModal}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" isLoading={loading}>
                  Update
                </Button>
              </div>
            </form>
          </>
        )}
      </Formik>
    </div>
  );
};

export default EditStaffAdmin;
















